<template>
  <locations-template>
    <div class="merchat-top-container" slot="content">
      <v-row class="merchat-top-container-title-label">
        <div class="text-title">All locations</div>
      </v-row>

      <v-row v-show="showSearchTabs">
        <v-tabs v-model="tab">
          <v-tab> {{ $t('map') }} </v-tab>
          <v-tab>{{ $t('payouts') }} </v-tab>
          <div class="d-flex justify-end grow button-panel">
            <period-picker
              v-if="isPayoutsTab"
              @periodUpdated="updatePayoutPeriodDates"
              :isDatePicker="false"
            />
          </div>
        </v-tabs>
      </v-row>

      <v-row
        v-if="showSearchTabs"
        :class="{
          'custom-row': showSearchTabs && tab === 0
        }"
      >
        <v-tabs-items v-model="tab">
          <v-tab-item class="tab-item">
            <v-divider class="with-horizontal-padding--map" />
            <map-form
              v-if="tab === 0"
              :zones="zones"
              :shouldZoomToCenter="shouldZoomToCenter"
              :focusedPoint="focusedPoint"
            />
          </v-tab-item>
          <v-tab-item class="tab-item">
            <v-divider style="margin-left: 56px; margin-right: 295px" />
            <payouts-table :payoutsList="payoutsData" />
          </v-tab-item>
        </v-tabs-items>
      </v-row>
      <v-row v-else class="with-horizontal-padding">
        <div class="sales-block pt-7">
          <div class="tab-row-title">Sales</div>
          <div class="tab-row d-flex align-end mb-9">
            <v-divider class="mr-7" />
            <period-picker
              allowAllTime
              :initialStartDate="searchByLicenceStartDate || ''"
              :initialEndDate="searchByLicenceEndDate || ''"
              @periodUpdated="updatePeriodDates"
            />
          </div>
          <licence-plate-search-table
            :parkingsList="searchedByLicencePlateList"
            :noDataText="licencePlateNoDataText"
            @loadMore="loadMoreResults"
          />
        </div>
      </v-row>
    </div>
  </locations-template>
</template>

<script>
import LocationsTemplate from '@/templates/Locations'
import MapForm from '@/components/Locations/AllLocations/MapForm.vue'
import LicencePlateSearchTable from '@/components/Locations/AllLocations/LicencePlateSearchTable.vue'
import PayoutsTable from '@/components/Locations/AllLocations/PayoutsTable.vue'
import PeriodPicker from '@/components/Common/PeriodPicker.vue'
import merchantHelpers from '@/helpers/merchant'
import * as config from '@/config/config'
import moment from 'moment'
import { mapActions, mapGetters } from 'vuex'
import eventBus from '@/helpers/eventBus'
import auth from '@/auth'

export default {
  name: 'MerchantLocationsView',
  components: {
    LocationsTemplate,
    MapForm,
    LicencePlateSearchTable,
    PeriodPicker,
    PayoutsTable
  },
  data() {
    return {
      isEditingUserInfo: false,
      isAllLocationsLoaded: false,
      isShowingDeleteAccountPopup: false,
      isDeletingUser: false,
      activeForm: 'map',
      zones: [],
      selectedButton: 'map',
      tab: '',
      shouldZoomToCenter: null,
      focusedPoint: null,
      restoringMapState: false,
      moment: moment,
      startDate: null,
      endDate: null,
      payoutsData: null
    }
  },
  computed: {
    ...mapGetters([
      'showSearchTabs',
      'searchedByLicencePlateList',
      'searchByLicenceStartDate',
      'searchByLicenceEndDate',
      'licencePlateSearchTerm',
      'allLocations',
      'filteredLocations',
      'mapViewState'
    ]),
    isPayoutsTab() {
      return this.tab === 1
    },
    licencePlateNoDataText() {
      if (!this.licencePlateSearchTerm) return ''
      return this.$t('no_parkings_found_for_plate', {
        plate: this.licencePlateSearchTerm
      })
    }
  },
  methods: {
    ...mapActions(['setSearchByLicencePeriod', 'setMapViewState']),
    async resetSidebarForAllMerchants() {
      // Ensure sidebar shows all locations when landing on /merchants, but preserve existing search/point if available
      const all = this.allLocations || []
      if (all.length) {
        await this.$store.dispatch('setMerchantLocations', all)
      }
      await this.$store.dispatch('clearFilteredLocationsData')

      const saved = this.mapViewState
      const hasSavedSearch = saved?.search || saved?.selectedPoint
      if (hasSavedSearch && saved && !saved.searchType) {
        await this.setMapViewState({
          ...saved,
          searchType: 'name'
        })
      }

      if (saved?.selectedPoint) {
        this.focusedPoint = { ...saved.selectedPoint }
        this.shouldZoomToCenter = null
      }
    },
    centerFromMapState() {
      const point = this.mapViewState?.selectedPoint
      if (!point) return
      const lat = parseFloat(point.lat || point.latitude)
      const lng = parseFloat(point.lng || point.longitude)
      if (!isNaN(lat) && !isNaN(lng)) {
        // Use a fresh object to trigger prop watchers downstream
        const focusPoint = { lat, lng }
        this.focusedPoint = focusPoint
        this.shouldZoomToCenter = null
        eventBus.$emit('centerMapToPoint', focusPoint)
      }
    },
    changeForm(form) {
      this.activeForm = form
      this.selectedButton = form
    },
    async init() {
      if (auth.isParkmanAdmin()) {
        const cachedLocations = this.allLocations || []

        // If we already have data (e.g., returning via browser back), reuse it without refetching.
        if (cachedLocations.length) {
          this.restoringMapState = true
          const savedMapView = this.mapViewState
          const preservedSearchType = savedMapView?.searchType || null
          const preservedSearch = savedMapView?.search || ''
          const preservedPoint = savedMapView?.selectedPoint || null
          await this.setMapViewState({
            searchType: preservedSearchType || 'name',
            search: preservedSearch,
            selectedPoint: preservedPoint
          })
          this.zones = cachedLocations
          await this.$store.dispatch('clearFilteredLocationsData')
          await this.$store.dispatch('setMerchantLocations', cachedLocations)
          await this.$store.dispatch('setSidebar', config.SIDEBAR.MERCHANT_ALL)
          this.shouldZoomToCenter = preservedPoint ? null : moment()
          this.focusedPoint = preservedPoint
          this.$nextTick(() => {
            setTimeout(() => this.centerFromMapState(), 0)
          })
          this.isAllLocationsLoaded = true
          this.restoringMapState = false
          return
        }

        await this.$store.dispatch('setShowOverlay', true)
        const locations = await merchantHelpers.getNearByLocations(
          '60.165707737184,24.930013198585'
        )
        this.zones = locations
        await this.$store.dispatch('setSidebar', config.SIDEBAR.MERCHANT_ALL)
        await this.$store.dispatch('setMerchantLocations', locations) // this.$store.getters.allLocations
        this.shouldZoomToCenter = moment()
        await this.$store.dispatch('setSystemMessage', {
          message: this.$t('other_locations_will_be_displayed_soon'),
          type: 'success'
        })
        await merchantHelpers.getAllAccountLocations()
        this.isAllLocationsLoaded = true
        await this.$store.dispatch('setShowOverlay', false)
      } else {
        const merchants = await merchantHelpers.fetchAllMerchants()
        await this.$router.push({
          path: `/merchants/${merchants[0].id}`
        })
      }
    },
    updatePeriodDates({ startDate, endDate }) {
      if (
        this.searchByLicenceStartDate !== startDate ||
        this.searchByLicenceEndDate !== endDate
      ) {
        this.setSearchByLicencePeriod({ startDate, endDate })
        eventBus.$emit('reloadLicencePlates')
      }
    },
    async updatePayoutPeriodDates({ startDate, endDate }) {
      const startTimeStamp = moment.utc(startDate, 'DD.MM.YYYY').unix()
      const endTimeStamp = moment.utc(endDate, 'DD.MM.YYYY').endOf('day').unix()

      await this.$store.dispatch('setShowOverlay', true)
      this.payoutsData = await merchantHelpers.fetchAllCountryPayouts(startTimeStamp, endTimeStamp)
      await this.$store.dispatch('setShowOverlay', false)
    },
    loadMoreResults() {
      eventBus.$emit('loadLicencePlates')
    },
    showPayoutsTab() {
      this.tab = 1
    },
    async getReports() {
      await this.$store.dispatch('setShowOverlay', true)
      this.payoutsData = await merchantHelpers.fetchAllCountryPayouts(1724404029, 1727082429)
      await this.$store.dispatch('setShowOverlay', false)
    }
  },
  watch: {
    allLocations(newVal) {
      this.$store.dispatch('setMerchantLocations', newVal)
      this.zones = newVal
    },
    filteredLocations(newVal) {
      this.zones = newVal.length ? newVal : this.$store.getters.allLocations
      // Avoid auto-fit when restoring a saved pin point
      if (this.restoringMapState && this.mapViewState?.selectedPoint) {
        this.shouldZoomToCenter = null
        return
      }
      this.shouldZoomToCenter = moment()
    },
    mapViewState: {
      deep: true,
      handler(state) {
        const point = state?.selectedPoint
        if (point) {
          const lat = parseFloat(point.lat || point.latitude)
          const lng = parseFloat(point.lng || point.longitude)
          if (!isNaN(lat) && !isNaN(lng)) {
            this.restoringMapState = true
            const focusPoint = { lat, lng }
            this.focusedPoint = focusPoint
            this.shouldZoomToCenter = null
            eventBus.$emit('centerMapToPoint', focusPoint)
            this.restoringMapState = false
          }
        }
      }
    },
    searchByLicenceStartDate(date) {
      this.startDate = date
    },
    searchByLicenceEndDate(date) {
      this.endDate = date
    },
    async tab(value) {
      if (value === 0 && this.$route.query.tab) {
        await this.init()
        this.$router.replace({ query: null })
      } else if (value === 1) {
        if (this.isAllLocationsLoaded === false) {
          await this.init()
        }
      }
    }
  },
  async beforeMount() {
    await this.$store.dispatch('setSidebar', config.SIDEBAR.MERCHANT_ALL)
    await this.resetSidebarForAllMerchants()
  },
  async mounted() {
    if (this.$route.query.tab) {
      this.showPayoutsTab()
    } else {
      await this.init()
    }
    eventBus.$on('centerMapToPoint', (point) => {
      this.focusedPoint = point
    })
    eventBus.$on('openPayouts', this.showPayoutsTab)
    await this.resetSidebarForAllMerchants()
    this.$nextTick(() => {
      setTimeout(() => this.centerFromMapState(), 0)
    })
  },
  beforeDestroy() {
    eventBus.$off('centerMapToPoint')
    eventBus.$off('openPayouts')
  }
}
</script>

<style lang="scss" scoped>
@import '~@/style/common';
@import '~@/style/variables';

.all-merchants-content {
  padding-top: $page-vertical-padding;
  text-align: start;
}
.custom-row {
  margin: 0px !important;
}
.tab-row {
  margin-top: -30px;
  &-title {
    color: $color-green;
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    line-height: 140%;
    letter-spacing: 0;
    min-width: 0;
    padding: 0;
    margin-right: 20px;
    text-transform: capitalize;
  }
}

.sales-block {
  width: 100%;
}

.button-panel {
  padding-right: 58px;
}

.payouts-tab-margins {
  margin-left: 44px;
  margin-right: 295px;
}
</style>
